import { useMemo } from 'react';
import { INDICATOR_CATEGORY, indicator_category_names } from '@voladeum/client';
import { CALENDAR_MODE } from 'framework/calendar/enums';
import { Option, OptionIndicator } from 'framework/Option';
import { Options } from 'framework/Options';
import { parseDate } from 'utils/dates';

import { EMPTY_FUNCTION } from '../../constants';

import { InputIcon } from './components/InputIcon';
import { InputDropdown } from './fields/InputDropdown';
import { Container, DropdownField, SelectCalendarField } from './Container';
import { DropdownCalendarProps, DropdownIconProps, DropdownProps } from './types';

export const Dropdown = <T extends Record<string, any>>(
  props: Omit<DropdownProps<T>, 'children'>
) => {
  return (
    <Container {...props}>
      <DropdownField>
        <InputDropdown />
      </DropdownField>
      <Options>
        <Option />
      </Options>
    </Container>
  );
};

export const DropdownIndicator = <T extends Record<string, any>>(
  props: Omit<DropdownProps<T>, 'children'>
) => {
  return (
    <Container {...props} keyboard_disabled>
      <DropdownField>
        <InputDropdown />
      </DropdownField>
      <Options>
        <OptionIndicator
          indicator={{
            indicator_category_id: INDICATOR_CATEGORY.PRICE,
            indicator_category_name: indicator_category_names[INDICATOR_CATEGORY.PRICE],
          }}
        />
        <OptionIndicator
          indicator={{
            indicator_category_id: INDICATOR_CATEGORY.MOVING_AVERAGE,
            indicator_category_name: indicator_category_names[INDICATOR_CATEGORY.MOVING_AVERAGE],
          }}
        />
        <OptionIndicator
          indicator={{
            indicator_category_id: INDICATOR_CATEGORY.MOMENTUM,
            indicator_category_name: indicator_category_names[INDICATOR_CATEGORY.MOMENTUM],
          }}
        />
        <OptionIndicator
          indicator={{
            indicator_category_id: INDICATOR_CATEGORY.VOLATILITY,
            indicator_category_name: indicator_category_names[INDICATOR_CATEGORY.VOLATILITY],
          }}
        />
        <OptionIndicator
          indicator={{
            indicator_category_id: INDICATOR_CATEGORY.SUPPORT_RESISTANCE,
            indicator_category_name:
              indicator_category_names[INDICATOR_CATEGORY.SUPPORT_RESISTANCE],
          }}
          last
        />
      </Options>
    </Container>
  );
};

export const DropdownIcon = <T extends Record<string, any>>({
  icon,
  extend = true,
  width = '40px',
  padding = '0px',
  ...props
}: Omit<DropdownIconProps<T>, 'children'>) => {
  return (
    <Container {...props} padding={padding} width={width} extend={extend} right_icon={null}>
      <DropdownField>
        <InputIcon icon={icon} />
      </DropdownField>
      <Options>
        <Option />
      </Options>
    </Container>
  );
};

export const DropdownCalendar = ({
  mode = CALENDAR_MODE.SINGLE,
  format = 'MM/DD/YYYY',
  min_date,
  max_date,
  onSelect = EMPTY_FUNCTION,
  ...rest
}: DropdownCalendarProps) => {
  const value = useMemo(() => {
    const [since, to] = parseDate(rest.value);
    if (mode === CALENDAR_MODE.RANGE && to) {
      return `${since} - ${to}`;
    }
    if (mode === CALENDAR_MODE.MULTIPLE) {
      const dates = parseDate(rest.value);
      return dates.join(', ');
    }
    return since;
  }, [mode, rest.value]);
  return (
    <Container {...rest} value={value}>
      <SelectCalendarField
        mode={mode}
        format={format}
        min_date={min_date}
        max_date={max_date}
        value={rest.value}
        onSelect={onSelect}
      >
        <InputDropdown />
      </SelectCalendarField>
    </Container>
  );
};
